"use client";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Icons } from "@/components/Icons";
import { Badge, Button, Card, Row, SectionHeader, TopBar } from "@/components/ui";
import { useOrder } from "@/hooks/useOrder";
import { useFormatPrice } from "@/hooks/useFormatPrice";
import { useMenu, localize, type UiMenuItem } from "@/hooks/useMenu";
import { Price } from "@/components/Price";
import { useCart, type CartLine } from "@/store/cart";
import { shouldAutoPromptReview, markReviewPrompted } from "@/lib/reviewPrompt";
import { reviewApi, canAttemptReview, type ReviewEligibility } from "@/api/reviewApi";
import { StarRating } from "@/components/reviews/StarRating";
import {
  OrderStatusEnum,
  OrderTypeEnum,
  PaymentStatusEnum,
  type OrderOutput,
  type OrderLineOutput,
  type SelectedModifierOption,
  type SelectedVariantOption,
} from "@/api/online-ordering-service/model";
import { getDisplayName } from "./CheckoutScreen";
import type { AppliedSurcharge } from "@/lib/surcharges";

/**
 * order.surcharges[] / pricingSummary.surchargeAmount (surcharge module)
 * aren't in the generated OrderOutput/OrderPricingSummaryOutput models yet —
 * order-service emits them, the OpenAPI client for this repo hasn't been
 * regenerated to include them. Extend minimally rather than editing the
 * generated file (`orderOutput.ts` is marked "Do not edit the class
 * manually").
 */
type OrderWithSurcharges = OrderOutput & {
  surcharges?: AppliedSurcharge[];
  pricingSummary?: OrderOutput["pricingSummary"] & { surchargeAmount?: number };
};

function getStageKeys(type: OrderTypeEnum | undefined | null) {
  const base = [
    { id: "received", key: "v2.order_detail.stages.received", fallback: "Order received" },
    { id: "kitchen", key: "v2.order_detail.stages.kitchen", fallback: "In the kitchen" },
  ];
  if (type === OrderTypeEnum.DineIn) {
    return [
      ...base,
      { id: "ready", key: "v2.order_detail.stages.served", fallback: "Served" },
    ];
  }
  if (type === OrderTypeEnum.Pickup) {
    return [
      ...base,
      { id: "ready", key: "v2.order_detail.stages.ready_for_pickup", fallback: "Ready for pickup" },
      { id: "picked_up", key: "v2.order_detail.stages.picked_up", fallback: "Picked up" },
    ];
  }
  return [
    ...base,
    { id: "ready", key: "v2.order_detail.stages.ready", fallback: "Ready" },
    { id: "delivery", key: "v2.order_detail.stages.delivery", fallback: "Out for delivery" },
    { id: "delivered", key: "v2.order_detail.stages.delivered", fallback: "Delivered" },
  ];
}

function stageIndex(status: OrderStatusEnum | undefined | null, type: OrderTypeEnum | undefined | null): number {
  if (!status) return 0;
  
  if (type === OrderTypeEnum.DineIn) {
    if (status === OrderStatusEnum.Completed || status === OrderStatusEnum.Delivered) return 2;
    if (status === OrderStatusEnum.Preparing) return 1;
    return 0;
  }
  if (type === OrderTypeEnum.Pickup) {
    if (status === OrderStatusEnum.Completed || status === OrderStatusEnum.Delivered) return 3;
    if (status === OrderStatusEnum.ReadyForPickup) return 2;
    if (status === OrderStatusEnum.Preparing) return 1;
    return 0;
  }
  
  if (status === OrderStatusEnum.Delivered || status === OrderStatusEnum.Completed) return 4;
  if (status === OrderStatusEnum.OutForDelivery) return 3;
  if (status === OrderStatusEnum.ReadyForPickup) return 2;
  if (status === OrderStatusEnum.Preparing) return 1;
  return 0;
}

function formatStatus(status: string | undefined | null): string {
  if (!status) return "";
  return status
    .replace(/[_-]/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function statusBadge(
  o: OrderOutput,
  t: (k: string) => string,
): { tone: "success" | "warn" | "danger" | "neutral"; label: string } {
  switch (o.status) {
    case OrderStatusEnum.Delivered:
    case OrderStatusEnum.Completed:
      return { tone: "success", label: t("v2.history.status.delivered") };
    case OrderStatusEnum.Cancelled:
      return { tone: "danger", label: t("v2.history.status.cancelled") };
    case OrderStatusEnum.Rejected:
      return { tone: "danger", label: t("v2.history.status.rejected") };
    case OrderStatusEnum.Draft:
      return { tone: "neutral", label: t("v2.history.status.draft") };
    case OrderStatusEnum.OutForDelivery:
      return { tone: "warn", label: t("v2.history.status.out_for_delivery") };
    case OrderStatusEnum.Preparing:
      return { tone: "warn", label: t("v2.history.status.preparing") };
    case OrderStatusEnum.ReadyForPickup:
      return { tone: "warn", label: t("v2.history.status.ready") };
    default:
      return { tone: "neutral", label: o.status ? formatStatus(o.status) : t("v2.history.status.pending") };
  }
}

function paymentBadge(
  s: PaymentStatusEnum | null | undefined,
  t: (k: string) => string,
): {
  tone: "success" | "warn" | "danger" | "neutral";
  label: string;
} {
  switch (s) {
    case PaymentStatusEnum.Paid:
      return { tone: "success", label: t("v2.order_detail.pay_status.paid") };
    case PaymentStatusEnum.PartiallyPaid:
      return { tone: "warn", label: t("v2.order_detail.pay_status.partial") };
    case PaymentStatusEnum.Refunded:
    case PaymentStatusEnum.PartiallyRefunded:
      return { tone: "neutral", label: t("v2.order_detail.pay_status.refunded") };
    case PaymentStatusEnum.Unpaid:
      return { tone: "danger", label: t("v2.order_detail.pay_status.unpaid") };
    default:
      return { tone: "neutral", label: t("v2.order_detail.pay_status.pending") };
  }
}

function resolveLineName(l: OrderLineOutput, menuItem: UiMenuItem | undefined, lang: string): string {
  return menuItem?.name || localize(l.itemName, lang) || "";
}

function resolveOptionName(
  menuItem: UiMenuItem | undefined,
  kind: "variant" | "modifier",
  groupId: string | null | undefined,
  optionId: string | null | undefined,
  lang: string,
  fallback: any,
): string {
  const groups = kind === "variant" ? menuItem?.raw?.variantGroups : menuItem?.raw?.modifierGroups;
  const group = (groups ?? []).find((g) => g.id === groupId) as any;
  const option = group?.options?.find((o: any) => o.id === optionId);
  const fromMenu = localize(option?.name ?? null, lang);
  if (fromMenu) return fromMenu;

  const fromOrder = localize(fallback, lang);
  if (fromOrder) return fromOrder;

  return "";
}

function formatAddress(a: NonNullable<OrderOutput["deliveryAddress"]>): string {
  const parts = [a.street, a.street2, a.city, a.state, a.postalCode, a.country].filter(Boolean);
  return parts.join(", ");
}

export function OrderDetailScreen() {
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();
  const { i18n, t } = useTranslation();
  const { order, loading, error, refresh } = useOrder(orderId);
  const { format } = useFormatPrice();
  const { items: menuItems } = useMenu();
  const { cart, addToCart, clearCart } = useCart();

  const lang = i18n.language || "en";

  // Aggressive review prompt: as soon as the poll observes this order sitting
  // in a Delivered/Completed state (and we haven't already prompted for it),
  // auto-navigate to the review screen. Gated once-per-order via localStorage
  // (src/lib/reviewPrompt.ts) so this fires at most once, ever, per order —
  // re-renders/re-polls of an already-prompted order are a no-op. Never fires
  // for Cancelled/Rejected. A stale prompt (e.g. already reviewed elsewhere)
  // degrades gracefully — ReviewSubmitScreen itself handles not-eligible/409.
  const autoPromptedRef = useRef(false);
  useEffect(() => {
    if (autoPromptedRef.current) return;
    if (!orderId || !order) return;
    if (!shouldAutoPromptReview({ orderId, status: order.status })) return;
    autoPromptedRef.current = true;
    markReviewPrompted(orderId);
    navigate(`/orders/${orderId}/review`);
  }, [orderId, order, navigate]);

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }),
    [i18n.language],
  );

  const reorder = (o: OrderOutput) => {
    const lines = o.orderLines ?? [];
    const builtLines: CartLine[] = [];
    let skipped = 0;

    for (const l of lines as OrderLineOutput[]) {
      const menuItem = menuItems.find((m) => m.id === l.itemId);
      if (!menuItem) {
        skipped++;
        continue;
      }
      const selectedModifiers: SelectedModifierOption[] = l.selectedModifiers ?? [];
      const selectedVariants: SelectedVariantOption[] = l.selectedVariants ?? [];

      const modsLabel: string[] = [];
      selectedVariants.forEach((v) => {
        const lbl = resolveOptionName(menuItem, "variant", v.variantGroupId, v.variantOptionId, lang, v.variantOptionName);
        if (lbl) modsLabel.push(lbl);
      });
      selectedModifiers.forEach((m) => {
        const lbl = resolveOptionName(menuItem, "modifier", m.modifierGroupId, m.modifierOptionId, lang, m.modifierOptionName);
        if (lbl) modsLabel.push(lbl);
      });

      const modifiersTotal = selectedModifiers.reduce(
        (s, m) => s + (m.unitPrice ?? 0) * (m.quantity ?? 1),
        0,
      );
      const variantAdjust = selectedVariants.reduce(
        (s, v) => s + (v.priceAdjustment ?? 0),
        0,
      );
      const qty = l.quantity ?? 1;
      // The cart stores a fully-configured per-unit price (cartTotal = price*qty)
      // and re-applies nothing. The order line already records the exact
      // configured price the customer paid, so use the recorded line subtotal
      // (base + variants + modifiers, pre-tax) ÷ qty. Re-adding variant/modifier
      // adjustments on top of l.unitPrice double-counted them (l.unitPrice is
      // ALREADY the configured price) — that doubled the reordered item price.
      const recordedSubtotal = l.pricingSummary?.subtotalAmount;
      const unitPrice =
        recordedSubtotal != null && qty > 0
          ? recordedSubtotal / qty
          : (l.unitPrice ?? menuItem.price + variantAdjust + modifiersTotal);

      builtLines.push({
        id: `${l.itemId}-${Date.now()}-${builtLines.length}`,
        itemId: l.itemId,
        name: resolveLineName(l, menuItem, lang),
        categoryId: menuItem.cat,
        categoryName: menuItem.categoryName,
        categoryNameRaw: menuItem.categoryNameRaw,
        price: unitPrice,
        qty,
        tone: menuItem.tone,
        selectedModifiers,
        selectedVariants,
        modsLabel,
        notes: l.notes ?? "",
      });
    }

    if (builtLines.length === 0) {
      window.alert(t("v2.history.reorder_none_available"));
      return;
    }
    if (cart.length > 0) {
      const ok = window.confirm(t("v2.history.reorder_replace_cart"));
      if (!ok) return;
      clearCart();
    }
    builtLines.forEach((line) => addToCart(line));
    if (skipped > 0) {
      window.alert(
        t(skipped === 1 ? "v2.history.reorder_skipped_one" : "v2.history.reorder_skipped_other", { count: skipped }),
      );
    }
    navigate("/cart");
  };

  if (loading && !order) {
    return (
      <div style={{ paddingBottom: 80 }}>
        <TopBar title={t("v2.order_detail.title")} onBack={() => navigate("/history")} />
        <div style={{ padding: 24, textAlign: "center", color: "var(--ink-3)" }}>
          {t("v2.order_detail.loading")}
        </div>
      </div>
    );
  }

  if (error && !order) {
    return (
      <div style={{ paddingBottom: 80 }}>
        <TopBar title={t("v2.order_detail.title")} onBack={() => navigate("/history")} />
        <Card padding={20} style={{ margin: 16, textAlign: "center" }}>
          <div className="display" style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>
            {t("v2.order_detail.couldnt_load")}
          </div>
          <div style={{ fontSize: 13, color: "var(--ink-2)", marginBottom: 12 }}>
            {error.message}
          </div>
          <Button variant="primary" size="sm" onClick={() => refresh()}>
            {t("v2.common.retry")}
          </Button>
        </Card>
      </div>
    );
  }

  if (!order) return null;

  const orderWithSurcharges = order as OrderWithSurcharges;
  const surcharges = orderWithSurcharges.surcharges ?? [];

  const badge = statusBadge(order, t);
  const stage = stageIndex(order.status, order.orderType);
  const ps = order.pricingSummary;
  const subtotal = ps?.itemsSubtotal ?? 0;
  const delivery = ps?.deliveryFeeAmount ?? 0;
  const tax = ps?.taxAmount ?? 0;
  const promoSaved = ps?.promotionAmount ?? 0;
  const discount = (ps?.discountAmount ?? 0) + (ps?.loyaltyDiscountAmount ?? 0);
  const total = ps?.totalAmount ?? order.totalPaid ?? 0;
  const tip = ps?.tipAmount ?? 0;
  const serviceFee = ps?.serviceFeeAmount ?? 0;
  const hasPromo = (order.appliedPromotionIds?.length ?? 0) > 0 || promoSaved > 0;

  const orderTimestamp = order.orderTimestamp ?? order.createdAt;
  const dateStr = orderTimestamp ? dateFmt.format(new Date(orderTimestamp)) : "";

  const primaryPayment = order.payments?.[0];
  const payMethod = primaryPayment?.method ?? order.selectedPaymentMethodType ?? "—";
  const payBadge = paymentBadge(order.paymentStatus, t);
  const cardInfo = (order.payments?.find((p) => p.cardDetails)?.cardDetails ?? null) as
    | { brand?: string; last4?: string; masked?: string }
    | null;
  const cardLabel = cardInfo
    ? `${cardInfo.brand ?? t("v2.order_detail.card", "Card")} •• ${cardInfo.last4 ?? cardInfo.masked ?? ""}`.trim()
    : null;
  const displayOrderRef = order.transactionNumber ?? order.orderNumber ?? order.id ?? "";

  const isDineIn = order.orderType === OrderTypeEnum.DineIn;
  const isReviewable =
    order.status === OrderStatusEnum.Completed || order.status === OrderStatusEnum.Delivered;

  return (
    <div style={{ paddingBottom: 80 }} className="order-detail-root">
      <style>{`
        @media print {
          .phone-shell-bottom-nav,
          [aria-label="Back"],
          .order-detail-actions,
          .order-detail-topbar { display: none !important; }
          .order-detail-root { padding: 0 !important; }
        }
      `}</style>
      <div className="order-detail-topbar">
        <TopBar title={t("v2.order_detail.order_number", { n: displayOrderRef })} onBack={() => navigate("/history")} />
      </div>

      <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 14 }}>
        <Card padding={14}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: 6,
            }}
          >
            <div>
              <div className="display" style={{ fontSize: 16, fontWeight: 700 }}>
                {t("v2.order_detail.order_number", { n: displayOrderRef })}
              </div>
              <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
                {dateStr}
              </div>
            </div>
            <Badge tone={badge.tone}>
              {badge.tone === "success" && <Icons.check size={10} />} {badge.label}
            </Badge>
          </div>
        </Card>

        <Card padding={14}>
          <SectionHeader title={t("v2.order_detail.status")} />
          <div style={{ display: "flex", flexDirection: "column" }}>
            {getStageKeys(order.orderType).map((s, i, arr) => {
              const done = i < stage;
              const cur = i === stage;
              return (
                <div key={s.id} style={{ display: "flex", gap: 12, paddingTop: i === 0 ? 0 : 8 }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <div
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 999,
                        background: done
                          ? "var(--primary-600)"
                          : cur
                            ? "var(--surface-1)"
                            : "var(--surface-2)",
                        border: cur ? "2px solid var(--primary-600)" : "0",
                        display: "grid",
                        placeItems: "center",
                        boxShadow: cur ? "0 0 0 5px var(--primary-50)" : "none",
                      }}
                    >
                      {done && <Icons.check size={12} stroke="#fff" sw={2.5} />}
                      {cur && (
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: 999,
                            background: "var(--primary-600)",
                          }}
                        />
                      )}
                    </div>
                    {i < arr.length - 1 && (
                      <div
                        style={{
                          width: 2,
                          flex: 1,
                          minHeight: 22,
                          background: done ? "var(--primary-600)" : "var(--border)",
                        }}
                      />
                    )}
                  </div>
                  <div style={{ flex: 1, paddingBottom: 8 }}>
                    <div className="display"
                      style={{
                        fontSize: 14,
                        fontWeight: cur ? 700 : 500,
                        color: cur || done ? "var(--ink-1)" : "var(--ink-3)",
                      }}
                    >
                      {t(s.key, s.fallback)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card padding={14}>
          <SectionHeader title={t("v2.order_detail.items")} />
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {(order.orderLines ?? []).map((l, i) => {
              const menuItem = menuItems.find((m) => m.id === l.itemId);
              const name = resolveLineName(l, menuItem, lang) || t("v2.order_detail.item_default");
              const linePrice = l.pricingSummary?.totalAmount ?? l.pricingSummary?.subtotalAmount ?? 0;
              return (
                <div
                  key={l.lineId ?? i}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    paddingBottom: i < (order.orderLines ?? []).length - 1 ? 12 : 0,
                    borderBottom: i < (order.orderLines ?? []).length - 1 ? "1px solid var(--border-light, #f1f5f9)" : "none",
                  }}
                >
                  {/* Item Image */}
                  <div style={{ position: "relative", width: 56, height: 56, flexShrink: 0, borderRadius: 8, overflow: "hidden", backgroundColor: "var(--surface-2, #f8fafc)", border: "1px solid var(--border-light, #e2e8f0)" }}>
                    {l.imageUrl ? (
                      <img
                        src={l.imageUrl}
                        alt={name}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
                        🍔
                      </div>
                    )}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink-1)" }}>
                        {l.quantity}× {name}
                      </div>
                      <div className="display" style={{ fontSize: 14, fontWeight: 700, whiteSpace: "nowrap", color: "var(--ink-1)" }}>
                        <Price amount={linePrice} />
                      </div>
                    </div>

                    {/* Modifiers and Variants with Images */}
                    {((l.selectedVariants ?? []).length > 0 || (l.selectedModifiers ?? []).length > 0) && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
                        {/* Selected Variants */}
                        {(l.selectedVariants ?? []).map((v, vIdx) => {
                          const vName = resolveOptionName(menuItem, "variant", v.variantGroupId, v.variantOptionId, lang, v.variantOptionName);
                          if (!vName) return null;
                          return (
                            <div key={vIdx} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              {v.imageUrl && (
                                <img
                                  src={v.imageUrl}
                                  alt={vName}
                                  style={{ width: 24, height: 24, borderRadius: 4, objectFit: "cover", border: "1px solid var(--border-light, #e2e8f0)" }}
                                />
                              )}
                              <span style={{ fontSize: 12, color: "var(--ink-2)", fontWeight: 500 }}>
                                {vName} {v.priceAdjustment ? <> (+<Price amount={v.priceAdjustment} />)</> : ""}
                              </span>
                            </div>
                          );
                        })}

                        {/* Selected Modifiers */}
                        {(l.selectedModifiers ?? []).map((m, mIdx) => {
                          const mName = resolveOptionName(menuItem, "modifier", m.modifierGroupId, m.modifierOptionId, lang, m.modifierOptionName);
                          if (!mName) return null;
                          const qtyStr = m.quantity && m.quantity > 1 ? `×${m.quantity}` : "";
                          return (
                            <div key={mIdx} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              {m.imageUrl && (
                                <img
                                  src={m.imageUrl}
                                  alt={mName}
                                  style={{ width: 24, height: 24, borderRadius: 4, objectFit: "cover", border: "1px solid var(--border-light, #e2e8f0)" }}
                                />
                              )}
                              <span style={{ fontSize: 12, color: "var(--ink-2)", fontWeight: 500 }}>
                                {qtyStr} {mName} {m.unitPrice ? <> (+<Price amount={m.unitPrice * (m.quantity ?? 1)} />)</> : ""}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {l.notes && (
                      <div
                        style={{
                          fontSize: 12,
                          color: "var(--ink-3)",
                          fontStyle: "italic",
                          marginTop: 4,
                        }}
                      >
                        {l.notes}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {order.notes && (
          <Card padding={14}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-2)", textTransform: "uppercase", marginBottom: 8 }}>
              {t("v2.order_detail.order_note", "Order note")}
            </div>
            <div style={{ fontSize: 14, color: "var(--ink-1)", lineHeight: 1.5 }}>
              {order.notes}
            </div>
          </Card>
        )}

        <Card padding={14}>
          <SectionHeader title={t("v2.order_detail.totals")} />
          <Row label={t("v2.order_detail.subtotal")} value={<Price amount={subtotal} />} />
          {delivery > 0 && <Row label={t("v2.order_detail.delivery")} value={<Price amount={delivery} />} />}
          {serviceFee > 0 && (
            <Row
              label={isDineIn ? t("v2.cart.service_tax") : t("v2.order_detail.service_fee")}
              value={<Price amount={serviceFee} />}
            />
          )}
          {surcharges.map((s) => (
            <Row
              key={s.surchargeId}
              label={
                <span>
                  {getDisplayName(s.name as { [k: string]: unknown }, lang)}
                  {s.waived && (
                    <span style={{ marginInlineStart: 6, fontSize: 11, color: "var(--ink-3)" }}>
                      ({t("v2.order_detail.surcharge_waived", "Waived")})
                    </span>
                  )}
                </span>
              }
              value={
                <span style={s.waived ? { textDecoration: "line-through", color: "var(--ink-3)" } : undefined}>
                  <Price amount={s.amount} />
                </span>
              }
            />
          ))}
          {tax > 0 && <Row label={t("v2.order_detail.tax")} value={<Price amount={tax} />} />}
          {tip > 0 && <Row label={t("v2.order_detail.tip")} value={<Price amount={tip} />} />}
          {hasPromo && promoSaved > 0 && (
            <Row label={t("v2.order_detail.promotion")} value={<span>−<Price amount={promoSaved} /></span>} />
          )}
          {discount > 0 && <Row label={t("v2.order_detail.discount")} value={<span>−<Price amount={discount} /></span>} />}
          <div
            style={{
              borderTop: "1px solid var(--border)",
              marginTop: 6,
              paddingTop: 6,
            }}
          >
            <Row label={t("v2.order_detail.total")} value={<Price amount={total} />} bold />
          </div>
        </Card>

        <Card padding={14}>
          <SectionHeader title={t("v2.order_detail.payment")} />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, textTransform: "capitalize" }}>
                {payMethod.replace(/_/g, " ")}
              </div>
              {cardLabel && (
                <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2, textTransform: "none" }}>
                  {cardLabel}
                </div>
              )}
            </div>
            <Badge tone={payBadge.tone}>{payBadge.label}</Badge>
          </div>
          {typeof order.totalPaid === "number" && order.totalPaid > 0 && (
            <div style={{ marginTop: 8 }}>
              <Row label={t("v2.order_detail.paid")} value={<Price amount={order.totalPaid} />} />
            </div>
          )}
          {typeof order.totalRefunded === "number" && order.totalRefunded > 0 && (
            <div style={{ marginTop: 4 }}>
              <Row
                label={t("v2.order_detail.refunded", "Refunded")}
                value={
                  <span style={{ color: "#16a34a", fontWeight: 700 }}>
                    −<Price amount={order.totalRefunded} />
                  </span>
                }
              />
            </div>
          )}
        </Card>

        <Card padding={14}>
          <SectionHeader title={isDineIn ? t("v2.order_detail.table") : t("v2.order_detail.delivery")} />
          {isDineIn ? (
            <div style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.5 }}>
              {order.tableNumber ? (
                <div>
                  {t("v2.order_detail.table")} <strong style={{ color: "var(--ink-1)" }}>{order.tableNumber}</strong>
                </div>
              ) : (
                <div>{t("v2.order_detail.dine_in")}</div>
              )}
              {order.seatingTimestamp && (
                <div style={{ marginTop: 4 }}>
                  {t("v2.order_detail.seated", { when: new Date(order.seatingTimestamp).toLocaleString(i18n.language) })}
                </div>
              )}
            </div>
          ) : order.deliveryAddress ? (
            <div style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.5 }}>
              <div style={{ color: "var(--ink-1)", fontWeight: 600 }}>
                {formatAddress(order.deliveryAddress)}
              </div>
              {order.deliveryInstructions && (
                <div style={{ marginTop: 4, fontStyle: "italic" }}>
                  {order.deliveryInstructions}
                </div>
              )}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: "var(--ink-3)" }}>{t("v2.order_detail.no_address")}</div>
          )}
        </Card>

        {isReviewable && (
          <OrderReviewCard orderId={orderId!} />
        )}

        <div
          className="order-detail-actions"
          style={{ display: "flex", gap: 10, marginTop: 4 }}
        >
          <Button variant="outline" size="md" full onClick={() => window.print()}>
            {t("v2.order_detail.print_receipt")}
          </Button>
          <Button variant="primary" size="md" full onClick={() => reorder(order)}>
            {t("v2.order_detail.reorder")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function OrderReviewCard({ orderId }: { orderId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [eligibility, setEligibility] = useState<ReviewEligibility | null>(null);

  useEffect(() => {
    // No customer identity (anonymous dine-in payer / unauthenticated
    // group-cart guest) → the BFF's eligibility route 401s unconditionally
    // (see reviewApi.canAttemptReview). Skip the request; render nothing.
    if (!canAttemptReview()) return;
    let active = true;
    reviewApi.getEligibility(orderId).then((res) => {
      if (active) setEligibility(res);
    }).catch((err) => {
      console.warn("Failed to fetch review eligibility for order detail", err);
    });
    return () => { active = false; };
  }, [orderId]);

  if (!eligibility) return null;

  if (eligibility.existingReview) {
    return (
      <Card padding={14}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 999,
              background: "var(--primary-50)",
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <Icons.star size={20} stroke="var(--primary-600)" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="display" style={{ fontSize: 14, fontWeight: 700, color: "var(--ink-1)" }}>
              {t("v2.reviews.entry.your_review_title", "Your Review")}
            </div>
            <div style={{ marginTop: 2 }}>
              <StarRating value={eligibility.existingReview.rating} readOnly size={14} justify="flex-start" />
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/orders/${orderId}/review`)}
          >
            {t("v2.reviews.entry.view_review", "View")}
          </Button>
        </div>
      </Card>
    );
  }

  if (eligibility.eligible) {
    return (
      <Card padding={14}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 999,
              background: "var(--primary-50)",
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <Icons.star size={20} stroke="var(--primary-600)" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="display" style={{ fontSize: 14, fontWeight: 700, color: "var(--ink-1)" }}>
              {t("v2.reviews.entry.card_title", "How was your order?")}
            </div>
            <div style={{ fontSize: 12.5, color: "var(--ink-2)", marginTop: 2 }}>
              {t("v2.reviews.entry.card_subtitle", "Leave a review and help us improve.")}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/orders/${orderId}/review`)}
          >
            {t("v2.reviews.entry.card_cta", "Review")}
          </Button>
        </div>
      </Card>
    );
  }

  return null;
}
